import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  theme,
  Box,
} from '@everlywell/ui-kit';
import React, { ReactNode } from 'react';

const BREAKPOINT_KEYS = Object.keys(theme.breakpoints);
const NUMBER_OF_BREAKPOINTS = BREAKPOINT_KEYS.length;

/**
 * Defines the structure for each element within the tabs array.
 */
export type TabbedAccordionElement = {
  /** The label for the tab. */
  label: string;
  /** The content for the tab. */
  content: ReactNode;
};

/**
 * Props for the TabbedAccordion component.
 */
export type TabbedAccordionProps = {
  /** An array of elements each containing a label and content for the tabs. */
  tabs: TabbedAccordionElement[];
  /** The index of the tab to be opened by default. Default is 0 */
  defaultIndex?: number;
  /** The breakpoint at which the component switches from accordion to tabs. Defaults to largeTablet */
  switchToTabBreakpoint?: keyof typeof theme.breakpoints;
  /** Background color for the tab/accordion button in its default state. Defaults to white */
  backgroundColor?: string;
  /** Background color for the tab/accordion button when active/selected. Defaults to viridian dark */
  activeBackgroundColor?: string;
  /** Text color for the tab/accordion button in its default state. Defaults to darkGrey */
  color?: string;
  /** Text color for the tab/accordion button when active/selected. Defaults to white */
  activeColor?: string;
  /** Icon color in its default state. Defaults to black */
  iconColor?: string;
  /** Icon color when the accordion is expanded or the tab is active. Defaults to white */
  activeIconColor?: string;
  /** Border color for each accordion or tab. */
  borderColor?: string;
  /** If true, tabs will fill the available space evenly. Defaults is true */
  tabsFillAvailableSpace?: boolean;
  onClickHeader?: (label: string) => void;
};

/**
 * A responsive component that displays as an accordion on smaller screens
 * and as tabs on larger screens, based on a specified breakpoint.
 *
 * @param {TabbedAccordionProps} props The props for the component.
 * @returns {JSX.Element} The TabbedAccordion component.
 */
const TabbedAccordion = ({
  tabs,
  defaultIndex = 0,
  switchToTabBreakpoint = 'largeTablet',
  backgroundColor = theme.colors.tints.white,
  activeBackgroundColor = theme.colors.viridian.dark,
  color = theme.colors.tints.darkGrey,
  activeColor = theme.colors.tints.white,
  iconColor = theme.colors.tints.darkGrey,
  activeIconColor = theme.colors.tints.white,
  borderColor = theme.colors.tints.lightGrey,
  tabsFillAvailableSpace = true,
  onClickHeader,
}: TabbedAccordionProps): JSX.Element => {
  // Get the index of the breakpoint to switch to tabs (larger screens)
  const breakpointIndex = BREAKPOINT_KEYS.indexOf(switchToTabBreakpoint);

  // Initialize display arrays for accordion
  let accordionDisplay = Array(NUMBER_OF_BREAKPOINTS).fill('none'); // Initially 'none' for all breakpoints
  accordionDisplay.fill('block', 0, breakpointIndex); // 'block' until just before the breakpointIndex

  // Initialize display arrays for tabs
  // Make sure the tabs are visible from switchToTabBreakpoint onwards
  let tabsDisplay = Array(NUMBER_OF_BREAKPOINTS).fill('none'); // Initially 'none' for all breakpoints
  tabsDisplay.fill('block', breakpointIndex); // 'block' from the breakpointIndex onwards

  return (
    <>
      {/* Accordion for mobile */}
      <Box display={accordionDisplay} data-testid="tabbed-accordion-accordion">
        <Accordion
          // If 'allowToggle' is passed, then 'index' or 'defaultIndex' must be an array
          defaultIndex={[defaultIndex]}
          size={'md' as never}
          variant={'secondary' as never}
          allowToggle
        >
          {tabs.map((tab, index) => (
            <AccordionItem key={index}>
              {({ isExpanded }) => (
                <>
                  <AccordionButton
                    onClick={() => onClickHeader?.(tab.label)}
                    backgroundColor={backgroundColor}
                    color={color}
                    borderColor={borderColor}
                    _expanded={{
                      backgroundColor: activeBackgroundColor,
                      color: activeColor,
                    }}
                    sx={{
                      // Styles for the chevron icon
                      svg: {
                        color: isExpanded ? activeIconColor : iconColor,
                      },
                    }}
                    fontWeight={theme.fontWeights.medium}
                    hidden={tabs?.length <= 1}
                  >
                    {tab.label}
                    <AccordionIcon />
                  </AccordionButton>

                  <AccordionPanel>{tab.content}</AccordionPanel>
                </>
              )}
            </AccordionItem>
          ))}
        </Accordion>
      </Box>

      {/* Tabs for desktop */}
      <Box display={tabsDisplay} data-testid="tabbed-accordion-tabs">
        <Tabs defaultIndex={defaultIndex} size={'md' as never}>
          <TabList hidden={tabs?.length <= 1}>
            {tabs.map((tab, index) => (
              <Tab
                onClick={() => onClickHeader?.(tab.label)}
                key={index}
                flex={tabsFillAvailableSpace ? 1 : undefined}
                backgroundColor={backgroundColor}
                color={color}
                borderColor={borderColor}
                _selected={{
                  backgroundColor: activeBackgroundColor,
                  color: activeColor,
                }}
                fontWeight={theme.fontWeights.medium}
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {tabs.map((tab, index) => (
              <TabPanel key={index}>{tab.content}</TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Box>
    </>
  );
};

export default TabbedAccordion;
